import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, Mail, Lock, Sparkles } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const APP_VERSION = 'v1.3.4.10';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isProcessingSession, setIsProcessingSession] = useState(false);

  // OAuth/SSO session_id callback handling
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      setIsProcessingSession(true);
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSession(sessionId);
    }
  }, []);

  const processSession = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (response.ok) {
        window.history.replaceState({}, document.title, '/dashboard');
        window.location.href = window.location.origin + '/dashboard';
      } else {
        throw new Error('Auth failed');
      }
    } catch (e) {
      toast.error('Giriş işlemi başarısız oldu');
      setIsProcessingSession(false);
      window.history.replaceState({}, document.title, '/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('E-posta ve şifre gerekli');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
        }
        toast.success('Giriş başarılı! Yönlendiriliyorsunuz...');
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        localStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          window.location.href = window.location.origin + (redirectUrl || '/dashboard');
        }, 600);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'E-posta veya şifre hatalı');
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  if (isProcessingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E1A]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl text-white font-medium">Giriş yapılıyor...</h2>
          <p className="text-gray-400 mt-2">Lütfen bekleyin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#0A0E1A] overflow-hidden relative">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }}></div>
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 left-1/3 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      ></div>

      {/* LEFT — Brand & Highlights (hidden on mobile) */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 xl:p-16 relative z-10">
        <div className="flex items-center gap-3">
          <img src="/onxflow_logo.png" alt="OnxFlow" className="w-11 h-11 rounded-xl shadow-lg shadow-cyan-500/30" />
          <div>
            <h1 className="text-white font-bold text-xl tracking-tight">OnxFlow</h1>
            <p className="text-cyan-400/80 text-xs">Proje Yönetimi</p>
          </div>
        </div>

        <div className="space-y-6 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-cyan-300 text-xs font-medium">{APP_VERSION} Sürümü</span>
          </div>
          <h2 className="text-5xl xl:text-6xl font-bold text-white leading-[1.15] pb-1">
            Süreçleriniz
            <br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              tek panelden
            </span>
            <br />
            yönetilsin.
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Görevler, dosyalar, ekipler, raporlar ve onay süreçleri — hepsi
            kontrolünüz altında, tek bir akışta.
          </p>

          {/* Feature stats */}
          <div className="grid grid-cols-3 gap-6 pt-6">
            <FeatureStat label="İş Akışı" value="Otomatik" />
            <FeatureStat label="Roller" value="RBAC" />
            <FeatureStat label="Snapshot" value="PDF + JSON" />
          </div>
        </div>

        <div className="text-gray-500 text-xs">
          © 2026 OnxFlow • Tüm hakları saklıdır
        </div>
      </div>

      {/* RIGHT — Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile logo (only shown on mobile) */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/onxflow_logo.png" alt="OnxFlow" className="w-11 h-11 rounded-xl shadow-lg shadow-cyan-500/30" />
            <div>
              <h1 className="text-white font-bold text-xl">OnxFlow</h1>
              <p className="text-cyan-400/80 text-xs">Proje Yönetimi</p>
            </div>
          </div>

          {/* Glass card */}
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2 leading-[1.2] pb-1">
                Hoş geldiniz
              </h2>
              <p className="text-gray-400 text-sm">
                Hesabınıza giriş yaparak devam edin
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 tracking-wider">
                  E-POSTA
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-cyan-400/60 focus:bg-white/[0.06] transition-all"
                    placeholder="ad@sirket.com"
                    autoComplete="email"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-2 tracking-wider">
                  ŞİFRE
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-11 pr-12 py-3.5 bg-white/[0.04] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-cyan-400/60 focus:bg-white/[0.06] transition-all"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-cyan-400 transition-colors"
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 py-3.5 px-6 bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-400/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 group"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Giriş yapılıyor...
                  </>
                ) : (
                  <>
                    Giriş Yap
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-white/5 text-center space-y-2">
              <p className="text-xs text-gray-500">
                Hesabınız yoksa, lütfen sistem yöneticimizle iletişime geçin.
              </p>
              <a
                href="mailto:onxflowtr@gmail.com"
                className="inline-block text-xs text-cyan-400 hover:text-cyan-300 transition-colors font-medium"
                data-testid="login-contact-email"
              >
                onxflowtr@gmail.com
              </a>
            </div>
          </div>

          {/* Mobile footer */}
          <div className="lg:hidden text-center text-gray-500 text-xs mt-8">
            © 2026 OnxFlow
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureStat({ label, value }) {
  return (
    <div className="border-l-2 border-cyan-400/30 pl-3">
      <div className="text-cyan-300 font-semibold text-sm">{value}</div>
      <div className="text-gray-500 text-xs mt-0.5">{label}</div>
    </div>
  );
}
